import React from 'react';
import { Grid } from '@bb-ui/react-library';
import { useLocation, useParams } from 'react-router';
import { JobInputBox } from './components/JobInputBox';
import { JobOutputBox } from './components/JobOutputBox';
import { useJobExecutionContext } from '../../contexts/JobExecutionContext';
import { JobErrorBox } from './components/JobErrorBox';
import { ExecutionBox } from './components/ExecutionBox';
import { RegistrationBox } from './components/RegistrationBox';
import { SummaryWidget } from './components/SummaryWidget';
import { JobChildrenBox } from './components/JobChildrenBox';
import { JobPipelineBox, hasStepEvents } from './components/JobPipelineBox';
import { JobLogsBox } from './components/JobLogsBox';
import { ACTIVE_STATUSES, ExecutionDetailResponseBody, Status } from '@lct-jobs/core';
import useRestApi from 'hooks/useRestApi';
import { useTenantContext } from 'contexts/TenantContext';
import { useLearnInstance } from 'hooks/useLearnInstance';
import { useJobRegion } from 'contexts/JobRegionContext';
import { useInterval } from 'hooks/useInterval';
import { POLL_MAX_AGE_MS } from 'hooks/useExecutionPolling';
import { useStyles } from '../JobList';
import { JobRegionPicker } from 'pages/Tenant/JobRegionPicker/JobRegionPicker';

const CHILD_JOBS_LIMIT = 500;
const MAX_AUTO_LOAD_PAGES = 50;
const POLLING_STATUSES: string[] = [...ACTIVE_STATUSES, Status.PENDING, Status.QUEUED];

const JobExecutionDetailGridItem: React.FC = (props) => (
  <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>{props.children}</Grid>
);

export const JobExecutionDetail: React.FC = () => {
  const classes = useStyles();
  const { executionId } = useParams<{ executionId: string }>();
  const { tenantId } = useTenantContext();
  const { learnInstanceId, resolving } = useLearnInstance();
  const { setExecutionId, executions } = useJobExecutionContext();
  const { lctApiUrl, isJobRegionReady, showRegionPicker } = useJobRegion();
  const [childJobs, setChildJobs] = React.useState<any[]>([]);
  const [autoLoadCount, setAutoLoadCount] = React.useState(0);

  React.useEffect(() => {
    setExecutionId(executionId);
  }, [executionId, setExecutionId]);

  const jobExecutionDetails = useRestApi<ExecutionDetailResponseBody>(
    lctApiUrl(`tenants/${tenantId}/jobManager/executions/${executionId}?limit=${CHILD_JOBS_LIMIT}`),
    { manual: true },
  );

  const siteJobExecutionDetails = useRestApi<ExecutionDetailResponseBody>(
    lctApiUrl(
      `tenants/${learnInstanceId}/jobManager/executions/${executionId}?limit=${CHILD_JOBS_LIMIT}`,
    ),
    { manual: true },
  );

  const jobFetch = jobExecutionDetails.fetch;
  const siteFetch = siteJobExecutionDetails.fetch;

  const { state } = useLocation<{ executionTenantId?: string } | undefined>();

  const listedTenantId = React.useMemo(
    () =>
      state?.executionTenantId ??
      executions.find((candidate) => candidate.executionId === executionId)?.tenantId,
    [state, executions, executionId],
  );

  React.useEffect(() => {
    if (!isJobRegionReady || resolving || !executionId) return;

    if (listedTenantId === tenantId) {
      jobFetch().catch(() => {});
      return;
    }
    if (listedTenantId && listedTenantId === learnInstanceId) {
      siteFetch().catch(() => {});
      return;
    }

    if (tenantId) {
      jobFetch().catch(() => {});
    }
    if (learnInstanceId) {
      siteFetch().catch(() => {});
    }
  }, [
    tenantId,
    learnInstanceId,
    listedTenantId,
    executionId,
    jobFetch,
    siteFetch,
    isJobRegionReady,
    resolving,
  ]);

  let jobExecutionData: ExecutionDetailResponseBody | undefined;
  let siteExecution = false;

  if (jobExecutionDetails.data) {
    jobExecutionData = jobExecutionDetails.data;
  } else if (siteJobExecutionDetails.data && learnInstanceId) {
    jobExecutionData = siteJobExecutionDetails.data;
    siteExecution = true;
  }

  React.useEffect(() => {
    setChildJobs([]);
    setAutoLoadCount(0);
  }, [executionId]);

  React.useEffect(() => {
    if (jobExecutionData?.children) {
      setChildJobs((prev) => {
        const existingIds = new Set(prev.map((child) => child.executionId));
        const newChildren = jobExecutionData!.children.filter(
          (child) => !existingIds.has(child.executionId),
        );
        if (newChildren.length === 0) return prev;
        return [...prev, ...newChildren];
      });
    }
  }, [jobExecutionData, jobExecutionData?.children]);

  const combinedExecutionData = React.useMemo(() => {
    if (!jobExecutionData) return undefined;

    return {
      ...jobExecutionData,
      children: childJobs,
    };
  }, [jobExecutionData, childJobs]);

  const loadChildJobs = React.useCallback(() => {
    const currentData = siteExecution ? siteJobExecutionDetails.data : jobExecutionDetails.data;
    const nextPageToken = currentData?.nextPageToken;

    if (nextPageToken) {
      if (siteExecution) {
        siteJobExecutionDetails
          .fetch({ params: { nextPageToken, limit: CHILD_JOBS_LIMIT } })
          .catch(() => {});
      } else {
        jobExecutionDetails
          .fetch({ params: { nextPageToken, limit: CHILD_JOBS_LIMIT } })
          .catch(() => {});
      }
    }
  }, [siteExecution, siteJobExecutionDetails, jobExecutionDetails]);

  React.useEffect(() => {
    const currentData = siteExecution ? siteJobExecutionDetails.data : jobExecutionDetails.data;
    const isLoading = siteExecution ? siteJobExecutionDetails.loading : jobExecutionDetails.loading;
    const nextPageToken = currentData?.nextPageToken;

    if (nextPageToken && !isLoading && autoLoadCount < MAX_AUTO_LOAD_PAGES) {
      setAutoLoadCount((prev) => prev + 1);
      loadChildJobs();
    }
  }, [
    jobExecutionData,
    siteExecution,
    siteJobExecutionDetails.data,
    siteJobExecutionDetails.loading,
    jobExecutionDetails.data,
    jobExecutionDetails.loading,
    autoLoadCount,
    loadChildJobs,
  ]);

  const refetchExecutionDetails = React.useCallback(() => {
    if (siteExecution) {
      siteJobExecutionDetails.fetch({ params: { limit: CHILD_JOBS_LIMIT } }).catch(() => {});
    } else {
      jobExecutionDetails.fetch({ params: { limit: CHILD_JOBS_LIMIT } }).catch(() => {});
    }
  }, [siteExecution, siteJobExecutionDetails, jobExecutionDetails]);

  // Skips a tick while the previous poll is still running, so a slow API can't pile up requests.
  // Holds the start time so a request that never settles is abandoned after POLL_MAX_AGE_MS.
  const pollStartedAt = React.useRef<number | null>(null);

  // useInterval keeps the timer stable; useRestApi returns a fresh object every render.
  useInterval(() => {
    if (!combinedExecutionData) return;
    if (pollStartedAt.current !== null && Date.now() - pollStartedAt.current < POLL_MAX_AGE_MS) {
      return;
    }

    const { status } = combinedExecutionData;
    const shouldFetch =
      POLLING_STATUSES.includes(status) ||
      (status === Status.PENDING_SCHEDULED &&
        combinedExecutionData.scheduledStartTime &&
        new Date(combinedExecutionData.scheduledStartTime) <= new Date());

    if (shouldFetch) {
      const request = siteExecution
        ? siteJobExecutionDetails.fetch({ params: { limit: CHILD_JOBS_LIMIT } })
        : jobExecutionDetails.fetch({ params: { limit: CHILD_JOBS_LIMIT } });
      const startedAt = Date.now();
      pollStartedAt.current = startedAt;
      request
        .catch(() => {})
        .finally(() => {
          if (pollStartedAt.current === startedAt) pollStartedAt.current = null;
        });
    }
  }, 10000);

  const hasPipeline = hasStepEvents(combinedExecutionData?.logs);
  const logsBox = (
    <JobLogsBox jobExecution={combinedExecutionData} hasMore={!!jobExecutionData?.nextPageToken} />
  );

  return (
    <>
      {showRegionPicker && (
        <div className={classes.controlsRow}>
          <JobRegionPicker />
        </div>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <SummaryWidget jobExecution={combinedExecutionData} />
        </Grid>
        <JobExecutionDetailGridItem>
          <RegistrationBox
            jobExecution={combinedExecutionData}
            detailsLoading={jobExecutionDetails.loading}
            siteDetailsLoading={siteJobExecutionDetails.loading}
          />
        </JobExecutionDetailGridItem>
        <JobExecutionDetailGridItem>
          <ExecutionBox
            jobExecution={combinedExecutionData}
            detailsLoading={jobExecutionDetails.loading}
            siteDetailsLoading={siteJobExecutionDetails.loading}
            onRescheduleSuccess={refetchExecutionDetails}
            onCancelSuccess={refetchExecutionDetails}
            onRedriveSuccess={refetchExecutionDetails}
            isLearnInstanceExecution={siteExecution}
          />
        </JobExecutionDetailGridItem>
        <JobExecutionDetailGridItem>
          <JobChildrenBox
            jobExecution={combinedExecutionData}
            detailsLoading={jobExecutionDetails.loading}
            siteDetailsLoading={siteJobExecutionDetails.loading}
            onLoadMore={loadChildJobs}
            hasMore={!!jobExecutionData?.nextPageToken}
            childLoadingError={
              siteExecution ? siteJobExecutionDetails.error : jobExecutionDetails.error
            }
          />
        </JobExecutionDetailGridItem>
        {/* Without step events the layout is unchanged: logs sit beside Input/Output. */}
        <JobExecutionDetailGridItem>
          {hasPipeline ? <JobPipelineBox jobExecution={combinedExecutionData} /> : logsBox}
        </JobExecutionDetailGridItem>
        <JobExecutionDetailGridItem>
          <JobInputBox jobExecution={combinedExecutionData} />
        </JobExecutionDetailGridItem>
        <JobExecutionDetailGridItem>
          <JobOutputBox jobExecution={combinedExecutionData} />
        </JobExecutionDetailGridItem>
        {combinedExecutionData?.exception && (
          <Grid size={{ xs: 12 }}>
            <JobErrorBox jobExecution={combinedExecutionData} />
          </Grid>
        )}
        {hasPipeline && <Grid size={{ xs: 12 }}>{logsBox}</Grid>}
      </Grid>
    </>
  );
};
